/* eslint-disable no-restricted-globals */
import { useContext } from 'react';
import PropTypes from 'prop-types';
import { RedirectTag, useHelmet } from '@thd-nucleus/thd-helmet';
import {
  number,
  params,
  shape,
  string,
  arrayOf,
  useDataModel
} from '@thd-nucleus/data-sources';
import { RedirectContext } from './RedirectContext';
import { isInvalidPageNumber, isInvalidReviewsCanonical } from './rules/review-redirect-rules';
import { isInvalidItemId, isNotFound } from './rules/pip-redirect-rules';
import { handleErrors } from '../utils/redirector-utils';

const reviewsHandler = ({ data, error, props }) => {
  const { itemId, pageNumber, bypass, path } = props;
  const { productError, reviewError } = error;
  const { productData, reviewData } = data;

  const invalidItemId = isInvalidItemId({ itemId });
  if (invalidItemId) {
    return new RedirectTag(invalidItemId, bypass);
  }

  const productErrors = productError ? handleErrors(productError) : null;
  if (productErrors) {
    return new RedirectTag(productErrors, bypass);
  }

  const notFound = isNotFound({ data: productData, itemId });
  if (notFound) {
    return new RedirectTag(notFound, bypass);
  }

  const invalidPageNumber = isInvalidPageNumber({ data: reviewData, pageNumber });
  if (invalidPageNumber) {
    return new RedirectTag(invalidPageNumber, bypass);
  }

  const checkReviewsCanonical = isInvalidReviewsCanonical({ data: productData, path, pageNumber });
  if (checkReviewsCanonical) {
    return new RedirectTag(checkReviewsCanonical, bypass);
  }

  return null;
};

export const ReviewsRedirector = ({ itemId }) => {

  const { pathWithQueryParams, bypass } = useContext(RedirectContext);
  const [path, queryParams] = pathWithQueryParams?.split('?') || '';

  const productOptions = {
    variables: {
      itemId
    },
    skip: !itemId
  };

  const pageSize = 30;
  const segments = path?.replace(/^\/|\/$/g, '').split('/');
  const pageNumber = segments?.length > 4 ? segments[segments.length - 1] : 1;
  const startIndex = !isNaN(pageNumber) ? Math.max((pageNumber - 1) * pageSize + 1, 1) : 1;
  const reviewsOptions = {
    variables: {
      itemId,
      pagesize: `${pageSize}`,
      recfirstpage: `${pageSize}`,
      startIndex
    },
    skip: !itemId || isNaN(pageNumber) || Number(pageNumber) < 1
  };

  const props = {
    itemId,
    pageNumber,
    bypass,
    path
  };

  const { data: productData, error: productError } = useDataModel('product', productOptions);
  const { data: reviewData, error: reviewError } = useDataModel('reviews', reviewsOptions);

  const data = { productData, reviewData };
  const error = { productError, reviewError };

  useHelmet('reviewsRedirector', { props, data, error }, reviewsHandler, [itemId, data, error]);

  return null;
};

ReviewsRedirector.displayName = 'ReviewsRedirector';

ReviewsRedirector.propTypes = {
  itemId: PropTypes.string.isRequired
};

ReviewsRedirector.defaultProps = {};

ReviewsRedirector.dataModel = {
  product: params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      canonicalUrl: string()
    })
  }),
  reviews: params({
    itemId: string().isRequired(),
    pagesize: string(),
    recfirstpage: string(),
    startIndex: number()
  }).shape({
    Results: arrayOf(shape({
      Rating: number(),
      ReviewText: string(),
      Title: string(),
      UserNickname: string()
    })),
    TotalResults: number()
  })
};
